import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

/* eslint-disable react/prop-types */
import React from 'react';
import ReactIs from 'react-is';
import styled from 'styled-components';
import useTabIndex from '../useTabIndex';
var Wrapper = styled.div.withConfig({
  displayName: "CheckboxRadioBase__Wrapper",
  componentId: "sc-1prkdgn-0"
})(["\n  flex: 0 0 auto;\n  position: relative;\n  display: inline-block;\n  vertical-align: middle;\n  ", ";\n"], _ref => {
  var inputWrapperStyle = _ref.inputWrapperStyle;
  return inputWrapperStyle;
});
var CustomInput = styled.span.withConfig({
  displayName: "CheckboxRadioBase__CustomInput",
  componentId: "sc-1prkdgn-1"
})(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  z-index: 0;\n  border: 1px solid #999;\n  border-radius: 2px;\n  background: #fff;\n  color: #000;\n  // The default margin is intentionally smaller on top so that the resulting\n  // vertical alignment looks good with most text's baseline.\n  margin: 2px 4px 4px 4px;\n  padding: 2px;\n  font-size: 10px;\n\n  input:active:not(:checked):not(:disabled) + && {\n    ", ";\n  }\n\n  input:checked + && {\n    border-color: #333;\n    background: #333;\n    color: #fff;\n    ", ";\n  }\n\n  input:active:checked:not(:disabled) + && {\n    ", ";\n  }\n\n  input:disabled + && {\n    ", ";\n  }\n\n  input:focus + && {\n    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.5),\n      0 0 0 3px ", ";\n    ", ";\n  }\n\n  // Give the inputStyle prop higher specificity than the single className it\n  // would get normally, so that this prop takes precedence over styles simply\n  // added with styled(Component).\n  && {\n    ", ";\n  }\n"], props => props.inputCheckingStyle, props => props.inputCheckedStyle, props => props.inputUncheckingStyle, props => props.inputDisabledStyle, props => props.theme.colors.focusColor, props => props.inputFocusStyle, props => props.inputStyle); // Workaround for `as` causing all the style-only props to be passed along to
// a component that then applies them to a DOM element because it doesn't know
// any better. Without this, the literal `checkmarkStyle` CSS would appear in
// the DOM as an attribute. - BB

function ApplyClassNameToChild(_ref2) {
  var className = _ref2.className,
      children = _ref2.children;
  return /*#__PURE__*/React.cloneElement(children, {
    className
  });
}

var Checkmark = styled(ApplyClassNameToChild).withConfig({
  displayName: "CheckboxRadioBase__Checkmark",
  componentId: "sc-1prkdgn-2"
})(["\n  flex: 0 0 auto;\n  visibility: hidden;\n\n  input:active:not(:checked):not(:disabled) + ", " > && {\n    visibility: visible;\n    opacity: 0.2;\n    ", ";\n  }\n\n  input:checked + ", " > && {\n    visibility: visible;\n    ", ";\n  }\n\n  input:active:checked:not(:disabled) + ", " > && {\n    opacity: 0.7;\n    ", ";\n  }\n\n  input:focus + ", " > && {\n    ", ";\n  }\n\n  input:disabled + ", " > && {\n    ", ";\n  }\n\n  // Give the inputStyle prop higher specificity than the single className it\n  // would get normally, so that this prop takes precedence over styles simply\n  // added with styled(Component).\n  && {\n    ", ";\n  }\n"], CustomInput, props => props.checkmarkCheckingStyle, CustomInput, props => props.checkmarkCheckedStyle, CustomInput, props => props.checkmarkUncheckingStyle, CustomInput, props => props.checkmarkFocusStyle, CustomInput, props => props.checkmarkDisabledStyle, props => props.checkmarkStyle);
var HiddenInput = styled.input.withConfig({
  displayName: "CheckboxRadioBase__HiddenInput",
  componentId: "sc-1prkdgn-3"
})(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  opacity: 0;\n  margin: 0;\n  z-index: 1;\n  cursor: pointer;\n\n  &:disabled {\n    cursor: not-allowed;\n  }\n"]);

/**
 * A base component for rendering custom checkbox or ratio input elements.
 *
 * Any remaining props not listed will be passed along to the hidden `<input>
 * underlying the custom element.
 */
export var CheckboxRadioBase = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  var style = _ref3.style,
      inputStyle = _ref3.inputStyle,
      inputCheckedStyle = _ref3.inputCheckedStyle,
      inputCheckingStyle = _ref3.inputCheckingStyle,
      inputDisabledStyle = _ref3.inputDisabledStyle,
      inputUncheckingStyle = _ref3.inputUncheckingStyle,
      inputFocusStyle = _ref3.inputFocusStyle,
      inputWrapperStyle = _ref3.inputWrapperStyle,
      checkmarkStyle = _ref3.checkmarkStyle,
      checkmarkCheckedStyle = _ref3.checkmarkCheckedStyle,
      checkmarkCheckingStyle = _ref3.checkmarkCheckingStyle,
      checkmarkDisabledStyle = _ref3.checkmarkDisabledStyle,
      checkmarkUncheckingStyle = _ref3.checkmarkUncheckingStyle,
      checkmarkFocusStyle = _ref3.checkmarkFocusStyle,
      rootClassName = _ref3.rootClassName,
      rootStyle = _ref3.rootStyle,
      className = _ref3.className,
      tabIndex = _ref3.tabIndex,
      CheckmarkIcon = _ref3.checkmarkIcon,
      disabled = _ref3.disabled,
      rest = _objectWithoutProperties(_ref3, ["style", "inputStyle", "inputCheckedStyle", "inputCheckingStyle", "inputDisabledStyle", "inputUncheckingStyle", "inputFocusStyle", "inputWrapperStyle", "checkmarkStyle", "checkmarkCheckedStyle", "checkmarkCheckingStyle", "checkmarkDisabledStyle", "checkmarkUncheckingStyle", "checkmarkFocusStyle", "rootClassName", "rootStyle", "className", "tabIndex", "checkmarkIcon", "disabled"]);

  tabIndex = useTabIndex(tabIndex);
  return /*#__PURE__*/React.createElement(Wrapper, {
    className: rootClassName,
    style: rootStyle,
    inputWrapperStyle: inputWrapperStyle
  }, /*#__PURE__*/React.createElement(HiddenInput, _extends({
    "data-hidden-input": "",
    ref: ref,
    tabIndex: tabIndex,
    disabled: disabled
  }, rest)), /*#__PURE__*/React.createElement(CustomInput, {
    "aria-hidden": "true",
    "data-custom-input": "",
    className: className,
    style: style,
    inputStyle: inputStyle,
    inputCheckedStyle: inputCheckedStyle,
    inputCheckingStyle: inputCheckingStyle,
    inputDisabledStyle: inputDisabledStyle,
    inputUncheckingStyle: inputUncheckingStyle,
    inputFocusStyle: inputFocusStyle
  }, /*#__PURE__*/React.createElement(Checkmark, {
    checkmarkStyle: checkmarkStyle,
    checkmarkCheckedStyle: checkmarkCheckedStyle,
    checkmarkCheckingStyle: checkmarkCheckingStyle,
    checkmarkDisabledStyle: checkmarkDisabledStyle,
    checkmarkUncheckingStyle: checkmarkUncheckingStyle,
    checkmarkFocusStyle: checkmarkFocusStyle
  }, ReactIs.isElement(CheckmarkIcon) ? /*#__PURE__*/React.cloneElement(CheckmarkIcon, {
    'data-checkmark': ''
  }) :
  /*#__PURE__*/
  // FIXME: JSX element type 'CheckmarkIcon' does not have any construct or call signatures.
  // @ts-expect-error
  React.createElement(CheckmarkIcon, {
    "data-checkmark": ""
  }))));
});
CheckboxRadioBase.displayName = 'CheckboxRadioBase';
var CheckboxRadioBaseNamespace = Object.assign(CheckboxRadioBase, {
  Wrapper,
  CustomInput,
  Checkmark,
  HiddenInput
});
export default CheckboxRadioBaseNamespace;